/**
 * EVENTSFLO-TENANT-EDITABLE-EMAIL-TEMPLATES-BILINGUAL-V1
 *
 * Settings → Portal Email Templates
 *
 * Tenant-admin page for editing the bilingual (EN + FR) client portal
 * invitation, password-reset, and magic-link email templates.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  listPortalEmailTemplates,
  getPortalEmailTemplateMeta,
  updatePortalEmailTemplate,
  resetPortalEmailTemplate,
  previewPortalEmailTemplate,
  sendTestPortalEmailTemplate,
  extractErrorMessage,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Loader2, Mail, RotateCcw, Save, Eye, Languages, Send } from "lucide-react";

// EVENTSFLO-ACCESS-INVITE-EMAIL-TEMPLATES-SEND-TEST-V1
// Default test recipient. Backend ALWAYS forces this inbox even if a
// different value is submitted, so admins cannot accidentally send real
// users a template-preview email. The input is informational; the server
// is the source of truth.
const SAFE_TEST_INBOX = "Eventsflo.test@hillsdon.com";

const VARIABLE_LABELS = {
  tenant_name: "Your tenant / company display name",
  client_name: "Client account name",
  recipient_name: "Recipient full name",
  recipient_email: "Recipient email",
  portal_url: "Base portal URL",
  setup_link: "Secure setup link (invite)",
  reset_link: "Secure password reset link",
  magic_link: "Secure one-tap login link",
  expiry_hours: "Hours until the link expires",
  support_email: "Tenant support email",
};

function VariableChip({ name }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(`{{${name}}}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      data-testid={`portal-template-var-${name}`}
      className="px-2 py-1 text-xs font-mono rounded border border-emerald-300/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition"
      title={VARIABLE_LABELS[name] || name}
    >
      {copied ? "Copied!" : `{{${name}}}`}
    </button>
  );
}

function TemplateEditor({ template, meta, onSaved }) {
  const [subject, setSubject] = useState(template.subject_template || "");
  const [body, setBody] = useState(template.body_template || "");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testRecipient, setTestRecipient] = useState(SAFE_TEST_INBOX);
  const [sendingTest, setSendingTest] = useState(false);
  const dirty =
    subject !== (template.subject_template || "") ||
    body !== (template.body_template || "");

  useEffect(() => {
    setSubject(template.subject_template || "");
    setBody(template.body_template || "");
    setPreview(null);
  }, [template.id]);

  const variables = useMemo(() => Object.keys(meta?.variables || {}), [meta]);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await updatePortalEmailTemplate(template.template_key, {
        subject_template: subject,
        body_template: body,
      });
      toast.success("Template saved");
      onSaved(data);
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Failed to save template");
    } finally {
      setSaving(false);
    }
  }, [template.template_key, subject, body, onSaved]);

  const onReset = useCallback(async () => {
    if (
      !window.confirm(
        "Reset this template to the bilingual default? Your edits will be lost."
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      const { data } = await resetPortalEmailTemplate(template.template_key);
      toast.success("Template reset to default");
      onSaved(data);
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Failed to reset template");
    } finally {
      setResetting(false);
    }
  }, [template.template_key, onSaved]);

  const onPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await previewPortalEmailTemplate(template.template_key, {
        subject_template: subject,
        body_template: body,
      });
      setPreview(data);
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Failed to render preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [template.template_key, subject, body]);

  const onSendTest = useCallback(async () => {
    setSendingTest(true);
    try {
      const { data } = await sendTestPortalEmailTemplate(
        template.template_key,
        { to_email: testRecipient || SAFE_TEST_INBOX },
      );
      toast.success(`Test email sent to ${data.to_email}`);
    } catch (e) {
      toast.error(
        extractErrorMessage(e) ||
          "Failed to send test email — check Email Configuration."
      );
    } finally {
      setSendingTest(false);
    }
  }, [template.template_key, testRecipient]);

  return (
    <div className="space-y-6" data-testid={`portal-template-editor-${template.template_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-emerald-200">
            {template.name}
          </h3>
          <p className="text-sm text-slate-400 max-w-2xl">
            {template.description}
          </p>
        </div>
        <Badge
          variant="secondary"
          className="bg-amber-500/10 text-amber-200 border border-amber-400/30"
          data-testid={`portal-template-bilingual-badge-${template.template_key}`}
        >
          <Languages className="w-3 h-3 mr-1" />
          EN + FR required (until preferred-language support ships)
        </Badge>
      </div>

      <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
        <Label className="text-xs uppercase tracking-wide text-slate-400">
          Available variables (click to copy)
        </Label>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {variables.map((v) => (
            <VariableChip key={v} name={v} />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        <Input
          id="subject"
          data-testid={`portal-template-subject-${template.template_key}`}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. You are invited to the {{tenant_name}} Client Portal / ..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="body">HTML body (EN + FR)</Label>
        <Textarea
          id="body"
          data-testid={`portal-template-body-${template.template_key}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="font-mono text-xs min-h-[360px]"
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid={`portal-template-save-${template.template_key}`}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save changes
        </Button>
        <Button
          variant="outline"
          onClick={onPreview}
          disabled={previewLoading}
          data-testid={`portal-template-preview-${template.template_key}`}
        >
          {previewLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-2" />
          )}
          Preview
        </Button>
        <Button
          variant="ghost"
          onClick={onReset}
          disabled={resetting}
          data-testid={`portal-template-reset-${template.template_key}`}
        >
          {resetting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="w-4 h-4 mr-2" />
          )}
          Reset to default
        </Button>
        {template.seeded_from_default ? (
          <Badge variant="outline" className="text-xs border-slate-600">
            Using default
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-xs border-emerald-500/40 text-emerald-200"
          >
            Customised (v{template.version || 1})
          </Badge>
        )}
      </div>

      {/* EVENTSFLO-ACCESS-INVITE-EMAIL-TEMPLATES-SEND-TEST-V1 */}
      <div
        className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
        data-testid={`portal-template-send-test-row-${template.template_key}`}
      >
        <Label
          htmlFor={`test-recipient-${template.template_key}`}
          className="text-xs uppercase tracking-wide text-amber-200/80"
        >
          Send a test render to the safe EventsFlo inbox
        </Label>
        <p className="text-xs text-slate-400 mt-1 mb-2">
          For security, the server <strong>always</strong> delivers test
          sends to{" "}
          <code className="text-amber-200">{SAFE_TEST_INBOX}</code>{" "}
          regardless of what is typed here. Sample variables are used —
          links are non-actionable.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            id={`test-recipient-${template.template_key}`}
            data-testid={`portal-template-test-recipient-${template.template_key}`}
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder={SAFE_TEST_INBOX}
            className="max-w-sm"
          />
          <Button
            onClick={onSendTest}
            disabled={sendingTest}
            variant="outline"
            className="border-amber-400/40 text-amber-200 hover:bg-amber-500/20"
            data-testid={`portal-template-send-test-${template.template_key}`}
          >
            {sendingTest ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send test email
          </Button>
        </div>
      </div>

      {preview && (
        <Card
          className="border-emerald-500/30 bg-slate-900/40"
          data-testid={`portal-template-preview-output-${template.template_key}`}
        >
          <CardHeader>
            <CardTitle className="text-sm text-emerald-200">
              Live preview
            </CardTitle>
            <CardDescription className="text-xs">
              Subject:{" "}
              <span
                className="font-mono"
                data-testid={`portal-template-preview-subject-${template.template_key}`}
              >
                {preview.subject}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="rounded border border-slate-700 bg-white text-slate-900 p-4 max-h-[480px] overflow-auto"
              data-testid={`portal-template-preview-body-${template.template_key}`}
              // Body is rendered HTML from the tenant-edited template. The
              // backend already strips unknown placeholders and HTML-escapes
              // free-text values. URLs come from controlled sample data.
              dangerouslySetInnerHTML={{ __html: preview.body }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function PortalEmailTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState(null);
  const [activeKey, setActiveKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, metaRes] = await Promise.all([
        listPortalEmailTemplates(),
        getPortalEmailTemplateMeta(),
      ]);
      const list = listRes.data.templates || [];
      setTemplates(list);
      setMeta(metaRes.data);
      if (list.length && !activeKey) {
        setActiveKey(list[0].template_key);
      }
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [activeKey]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaved = (updated) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.template_key === updated.template_key ? { ...t, ...updated } : t
      )
    );
  };

  return (
    <div
      className="container mx-auto px-4 py-8 max-w-5xl"
      data-testid="portal-email-templates-page"
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-100">
          <Mail className="w-7 h-7 text-emerald-400" />
          Access &amp; Invite Email Templates
        </h1>
        <p className="text-slate-400 mt-1 text-sm max-w-2xl">
          Edit the wording your tenant sends when inviting{" "}
          <strong className="text-slate-300">client portal</strong> users
          OR <strong className="text-slate-300">crew / staff</strong>{" "}
          members, resetting their passwords, sending a magic login link,
          or asking crew to install the PWA. Templates currently include
          English + French content side-by-side until per-recipient
          language preference is released.
        </p>
        <p className="text-slate-500 mt-2 text-xs max-w-2xl">
          Not covered here: quote / invoice send emails (edit those at
          {" "}<code className="text-emerald-300">/settings/email-templates</code>),
          crew onboarding document reminders &amp; expiry alerts
          (editable from the Crew Email Editor on the staff/crew detail
          page), and operational shift / push-to-app notifications.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading templates…
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-slate-400">
            No portal templates available.
          </CardContent>
        </Card>
      ) : (
        <Tabs
          value={activeKey || templates[0].template_key}
          onValueChange={setActiveKey}
          data-testid="portal-templates-tabs"
        >
          <TabsList className="flex-wrap h-auto">
            {templates.map((t) => (
              <TabsTrigger
                key={t.template_key}
                value={t.template_key}
                data-testid={`portal-template-tab-${t.template_key}`}
              >
                {t.name.replace(" (Bilingual)", "")}
              </TabsTrigger>
            ))}
          </TabsList>
          {templates.map((t) => (
            <TabsContent
              key={t.template_key}
              value={t.template_key}
              className="mt-6"
            >
              <TemplateEditor
                template={t}
                meta={meta}
                onSaved={handleSaved}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
